import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Play, MessageCircle, ArrowRight } from 'lucide-react';
import '../styles.css';

function ProductDetails() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [settings, setSettings] = useState({ telegram_link: '#', telegram_username: '@admin' });
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [images, setImages] = useState([]);
  const similarTrackRef = useRef(null);

  const scrollSimilar = (direction) => {
    const track = similarTrackRef.current;
    if (!track) return;

    const isMobile = window.innerWidth <= 900;
    const scrollAmount = isMobile ? track.clientWidth : track.clientWidth / 3;

    track.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    Promise.all([
      axios.get(`/api/products/${id}`),
      axios.get('/api/products'),
      axios.get('/api/settings')
    ]).then(([prodRes, allProdRes, settRes]) => {
      // Process current product
      const productData = prodRes.data;
      setProduct(productData);
      setSettings(settRes.data);
      
      // Process similar products
      const allProducts = allProdRes.data;
        const related = allProducts
          .filter(p => p.category === productData.category && p.id !== productData.id);
      setSimilarProducts(related);
      
      let parsedImages = [];
      const imageVal = productData.image_url;
      if (imageVal) {
        if (typeof imageVal === 'string' && imageVal.trim().startsWith('[')) {
          try {
            parsedImages = JSON.parse(imageVal);
          } catch(e) {
            parsedImages = [imageVal];
          }
        } else {
          parsedImages = [imageVal];
        }
      }
      setImages(parsedImages.filter(Boolean));
      setLoading(false);
    }).catch(error => {
      console.error("Ошибка при загрузке данных:", error);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return <div className="app-container"><div className="loading">Загрузка данных...</div></div>;
  }

  if (!product) {
    return (
      <div className="app-container">
        <div className="loading" style={{color: 'red'}}>Товар не найден</div>
        <div style={{textAlign: 'center', marginTop: '20px'}}>
             <Link to="/" className="btn btn-primary" style={{display: 'inline-flex'}}>На главную</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="grid-bg"></div>

      <nav>
        <Link to="/" className="logo">YOONIX<span className="dot">.</span><span>SHOP</span></Link>
        <div className="nav-links">
           <Link to="/" className="btn btn-primary btn-nav"><ArrowLeft size={16} style={{marginRight: '5px'}}/> Назад</Link>
        </div>
      </nav>

      <div className="product-details-page">
          <div className="product-details-container">
              {/* Левая колонка - Галерея */}
              <div className="product-gallery">
                  <div className="main-image-wrap">
                      {images.length > 0 ? (
                          <img src={images[activeImage]} alt="Main" className="main-image" />
                      ) : (
                          <div className="main-image-placeholder">[ НЕТ СИГНАЛА ]</div>
                      )}
                      
                      <div className="tag-container" style={{top: '20px', left: '20px'}}>
                          {product.is_hit ? <span className="tag new">ХИТ</span> : null}
                          {product.is_new ? <span className="tag new" style={{backgroundColor: '#fff'}}>NEW</span> : null}
                          <span className={`tag ${product.category}`}>
                            {product.category.toUpperCase()}
                          </span>
                      </div>
                  </div>
                  
                  {images.length > 1 && (
                      <div className="thumbnail-list">
                          {images.map((img, idx) => (
                              <div 
                                  key={idx} 
                                  className={`thumbnail ${activeImage === idx ? 'active' : ''}`}
                                  onClick={() => setActiveImage(idx)}
                              >
                                  <img src={img} alt={`Thumb ${idx}`} />
                              </div>
                          ))}
                      </div>
                  )}
              </div>

              {/* Правая колонка - Инфо */}
              <div className="product-info-wrap">
                  <h1 className="product-title">{product.title}</h1>
                  
                  <div className="product-price">
                        {Math.floor(product.price).toLocaleString()} <span>TJS</span>
                  </div>

                  <div className="product-desc-full">
                      {product.description.split('\n').map((line, i) => (
                          <p key={i}>{line}</p>
                      ))}
                  </div>

                  <div className="product-actions-big">
                      <a href={`${settings.telegram_link}?text=Здравствуйте! Хочу купить: ${product.title} (ID: ${product.id})`} target="_blank" rel="noreferrer" className="btn btn-primary buy-btn-big">
                          <MessageCircle size={22} /> Написать продавцу
                      </a>
                      
                      {product.link_demo && (
                          <a href={product.link_demo} target="_blank" rel="noreferrer" className="btn-demo-big">
                              <Play size={20} /> Смотреть демо проекта
                          </a>
                      )}
                  </div>
                  
                  <div className="product-guarantee">
                      <div className="guarantee-item">
                          <h3>⚡ Мгновенный доступ</h3>
                          <p>После оплаты мы сразу передаем вам все исходники и доступы.</p>
                      </div>
                      <div className="guarantee-item">
                          <h3>🛠 Поддержка запуска</h3>
                          <p>Поможем развернуть проект на вашем сервере, если нужно.</p>
                      </div>
                  </div>
              </div>
          </div>
          
          {similarProducts.length > 0 && (
            <div className="similar-products" style={{marginTop: '60px'}}>
              <div className="similar-products-head">
                <h2 style={{color: 'white', marginBottom: '0', fontFamily: '"Space Grotesk", sans-serif'}}>ПОХОЖИЕ ТОВАРЫ</h2>
                <div className="similar-controls">
                  <button type="button" className="similar-arrow" onClick={() => scrollSimilar(-1)} aria-label="Прокрутить похожие товары влево">
                    <ArrowLeft size={16} />
                  </button>
                  <button type="button" className="similar-arrow" onClick={() => scrollSimilar(1)} aria-label="Прокрутить похожие товары вправо">
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
              <div className="similar-products-track" ref={similarTrackRef}>
                {similarProducts.map((simProd) => {
                  let thumbImage = '';
                  let simImages = [];
                  const sVal = simProd.image_url;
                  if (sVal) {
                    if (typeof sVal === 'string' && sVal.trim().startsWith('[')) {
                      try {
                        simImages = JSON.parse(sVal);
                        thumbImage = simImages[0];
                      } catch {
                        thumbImage = sVal;
                      }
                    } else {
                      thumbImage = sVal;
                    }
                  } else {
                      thumbImage = 'https://placehold.co/400x300/333/fff?text=Нет+фото';
                  }

                  return (
                    <div className="card similar-card" key={simProd.id}>
                      <div className="card-img" style={{ backgroundImage: `url(${thumbImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                        <div className="tag-container">
                          {simProd.is_hit ? <span className="tag new">ХИТ</span> : null}
                          {simProd.is_new ? <span className="tag new" style={{backgroundColor: '#fff'}}>NEW</span> : null}
                          <span className={`tag ${simProd.category}`}>
                            {simProd.category.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="card-body">
                        <h3 className="card-title">{simProd.title}</h3>
                        <p className="card-desc">
                          {simProd.description.length > 90 
                            ? simProd.description.substring(0, 90) + '...' 
                            : simProd.description}
                        </p>

                        <div className="card-footer">
                          <div className="price">
                            {Math.floor(simProd.price).toLocaleString()} <span>TJS</span>
                          </div>
                        </div>

                        <div className="action-group">
                          <Link to={`/product/${simProd.id}`} className="btn-card btn-demo" style={{ borderColor: 'var(--lime)', color: 'var(--lime)', gridColumn: 'span 2' }}>
                            ПОДРОБНЕЕ <ArrowRight size={14} style={{marginLeft: 'auto'}}/>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
      </div>

      <footer>
        <div className="footer-grid">
          <div className="logo" style={{fontSize: '1.2rem'}}>YOONIX<span className="dot">.</span><span>SHOP</span></div>
        </div>
        <div className="copy">
          Система активна. © {new Date().getFullYear()} Yoonix.shop v2.0
        </div>
      </footer>
    </div>
  );
}

export default ProductDetails;
